import { useState } from "react";
import { Instagram, Mail, MapPin, Phone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { contact } from "@/lib/site-data";

const infoBlocks = [
  {
    icon: Phone,
    label: "Zavolajte nám",
    value: contact.phone,
    href: `tel:${contact.phoneHref}`,
  },
  {
    icon: Mail,
    label: "Napíšte nám",
    value: contact.email,
    href: `mailto:${contact.email}`,
  },
  {
    icon: Instagram,
    label: "Sledujte nás",
    value: "@artbaz.stavebnafirma",
    href: contact.instagram,
    external: true,
  },
  {
    icon: MapPin,
    label: "Adresa",
    value: `${contact.address[0]}, ${contact.address[1]}`,
  },
];

const inputClass =
  "w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";

export function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const onChange = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent) return;
    setStatus("sending");
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: "553191a7-5de6-4375-9ce1-2793cb153e5a",
          subject: `Dopyt z webu — ${form.name || "Nový kontakt"}`,
          from_name: form.name || "Web ARTBAZ",
          name: form.name,
          phone: form.phone,
          email: form.email,
          service: form.service,
          message: form.message,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus("success");
        setForm({ name: "", phone: "", email: "", service: "", message: "" });
        setConsent(false);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="kontakt" className="scroll-mt-20 bg-background py-12 md:py-16">
      <div className="container-artbaz">
        <div className="content-artbaz grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left — info */}
          <Reveal>
            <span className="eyebrow">Kontakt</span>
            <h2 className="mt-6 text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Kontaktujte nás
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">
              Máte otázku, plánujete rekonštrukciu alebo chcete nezáväznú cenovú ponuku? Napíšte
              nám alebo nám zavolajte. Radi s vami preberieme váš projekt a navrhneme najlepšie
              riešenie.
            </p>

            <ul className="mt-10 space-y-6">
              {infoBlocks.map((block) => {
                const Icon = block.icon;
                const content = (
                  <>
                    <span className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-[var(--shadow-primary)]">
                      <Icon className="size-6" />
                    </span>
                    <span>
                      <span className="block text-xs font-bold uppercase tracking-[0.14em] text-primary-dark">
                        {block.label}
                      </span>
                      <span className="mt-1 block text-base font-semibold text-foreground">
                        {block.value}
                      </span>
                    </span>
                  </>
                );
                return (
                  <li key={block.label}>
                    {block.href ? (
                      <a
                        href={block.href}
                        target={block.external ? "_blank" : undefined}
                        rel={block.external ? "noopener noreferrer" : undefined}
                        className="flex items-center gap-4 transition-opacity hover:opacity-80"
                      >
                        {content}
                      </a>
                    ) : (
                      <div className="flex items-center gap-4">{content}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </Reveal>

          {/* Right — form panel */}
          <Reveal delay={120}>
            <form
              onSubmit={onSubmit}
              className="rounded-2xl bg-secondary p-7 shadow-[var(--shadow-card)] sm:p-10"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="c-name" className="mb-2 block text-sm font-bold text-foreground">
                    Meno
                  </label>
                  <input
                    id="c-name"
                    type="text"
                    required
                    maxLength={120}
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Vaše meno"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="c-phone" className="mb-2 block text-sm font-bold text-foreground">
                    Telefón
                  </label>
                  <input
                    id="c-phone"
                    type="tel"
                    maxLength={40}
                    value={form.phone}
                    onChange={onChange("phone")}
                    placeholder="Telefónne číslo"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="c-email" className="mb-2 block text-sm font-bold text-foreground">
                    Email
                  </label>
                  <input
                    id="c-email"
                    type="email"
                    required
                    maxLength={160}
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="vas@email.sk"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="c-service" className="mb-2 block text-sm font-bold text-foreground">
                    Typ služby
                  </label>
                  <input
                    id="c-service"
                    type="text"
                    maxLength={120}
                    value={form.service}
                    onChange={onChange("service")}
                    placeholder="Napr. rekonštrukcia bytu"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="c-message" className="mb-2 block text-sm font-bold text-foreground">
                  Správa
                </label>
                <textarea
                  id="c-message"
                  required
                  rows={6}
                  maxLength={2000}
                  value={form.message}
                  onChange={onChange("message")}
                  placeholder="Ako vám môžeme pomôcť?"
                  className={`${inputClass} resize-y`}
                />
              </div>

              <label className="mt-7 flex items-start gap-3 text-sm leading-relaxed text-muted-foreground">
                <input
                  type="checkbox"
                  required
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 size-5 shrink-0 cursor-pointer rounded border-border accent-primary"
                />
                <span>
                  Súhlasím so spracúvaním mojich osobných údajov (meno, telefón, e-mail a obsah
                  správy) za účelom spätného kontaktovania a vybavenia môjho dopytu v súlade s
                  Nariadením (EÚ) 2016/679 (GDPR) a zákonom č. 18/2018 Z. z. o ochrane osobných
                  údajov. Súhlas je dobrovoľný a môžem ho kedykoľvek odvolať.
                </span>
              </label>

              <Button
                type="submit"
                variant="hero"
                size="lg"
                disabled={!consent || status === "sending"}
                className="mt-7 w-full disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {status === "sending" ? "Odosielam…" : "Odoslať správu"}
              </Button>

              {status === "success" && (
                <p className="mt-4 text-sm font-semibold text-primary-dark">
                  Ďakujeme! Vaša správa bola úspešne odoslaná. Čoskoro sa vám ozveme.
                </p>
              )}
              {status === "error" && (
                <p className="mt-4 text-sm font-semibold text-destructive">
                  Správu sa nepodarilo odoslať. Skúste to prosím znova alebo nám zavolajte.
                </p>
              )}



            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
